import { useState } from "react";

import Button from "../components/ui/Button";
import { CardHeading } from "../components/ui/Card";
import Logo from "../components/ui/Logo";
import { cn } from "../lib/cn";
import {
  confirmSignInOtp,
  confirmSignUpOtp,
  requestEmailOtp,
  resendEmailOtp,
  type OtpMode,
} from "./otp";

const SIGNUP_CODE_LENGTH = 6;
const SIGNIN_CODE_LENGTH = 8;

type Step = "email" | "verify";

export default function LoginScreen({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState<OtpMode>("signin");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const codeLength = mode === "signup" ? SIGNUP_CODE_LENGTH : SIGNIN_CODE_LENGTH;

  async function onSubmitEmail(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const otpMode = await requestEmailOtp(email.trim());
      setMode(otpMode);
      setCode("");
      setStep("verify");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const trimmed = code.trim();
      if (mode === "signin") {
        await confirmSignInOtp(trimmed);
      } else {
        await confirmSignUpOtp(email.trim(), trimmed);
      }
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setResending(true);
    setError(null);
    try {
      await resendEmailOtp(email.trim(), mode);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setResending(false);
    }
  }

  function backToEmail() {
    setStep("email");
    setCode("");
    setError(null);
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-6 p-6">
      <Logo className="h-[18px] w-[52px] text-ink-strong" />
      {step === "email" ? (
        <form onSubmit={onSubmitEmail} className="card w-full max-w-sm">
          <CardHeading
            title="Jiva Admin — sign in"
            caption="Enter your email and we'll send you a verification code."
          />
          <div className="flex flex-col gap-4 px-7 pb-7">
            <label className="flex flex-col gap-1.5">
              <span className={FIELD_LABEL}>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={FIELD}
                autoFocus
                required
              />
            </label>
            {error && <ErrorLine message={error} />}
            <Button type="submit" disabled={busy} className="w-full">
              {busy ? "Sending code…" : "Send code"}
            </Button>
          </div>
        </form>
      ) : (
        <form onSubmit={onSubmitCode} className="card w-full max-w-sm">
          <CardHeading
            title="Enter verification code"
            caption={`We sent a ${codeLength}-digit code to ${email}.`}
          />
          <div className="flex flex-col gap-4 px-7 pb-7">
            <label className="flex flex-col gap-1.5">
              <span className={FIELD_LABEL}>Code</span>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\s/g, ""))}
                maxLength={codeLength}
                className={cn(FIELD, "font-mono tracking-widest")}
                autoFocus
                required
              />
            </label>
            {error && <ErrorLine message={error} />}
            <Button type="submit" disabled={busy || code.length !== codeLength} className="w-full">
              {busy ? "Verifying…" : "Verify"}
            </Button>
            <div className="flex justify-between">
              <Button variant="link" onClick={backToEmail}>
                Use a different email
              </Button>
              <Button variant="link" onClick={onResend} disabled={resending}>
                {resending ? "Sending…" : "Resend code"}
              </Button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}

/** An input, in the `.control` box the eval dashboard's fields use. */
const FIELD = "control h-10 w-full px-3 text-cell text-ink outline-none";
const FIELD_LABEL = "text-cell font-medium text-ink-3";

function ErrorLine({ message }: { message: string }) {
  return (
    <p role="alert" className="text-body-sm text-[color:var(--acuity-4-fg)]">
      {message}
    </p>
  );
}
